import { Typography, withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React from 'react';

const railStyle = (theme) => ({
  root: {
    position: 'absolute',
    width: '100%',
    height: 14,
    borderRadius: 7,
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[500],
    '@media print': {
      border: '1px solid black',
      WebkitPrintColorAdjust: 'exact'
    }
  }
});

function RailComponent({ classes, getRailProps }) {
  return <div className={classes.root} {...getRailProps()} />;
}

RailComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  getRailProps: PropTypes.func.isRequired
};

export const Rail = withStyles(railStyle)(RailComponent);

const handleStyle = (theme) => ({
  root: {
    position: 'absolute',
    marginLeft: '-11px',
    marginTop: '-6px',
    zIndex: 2,
    width: 24,
    height: 24,
    cursor: 'pointer',
    borderRadius: '50%',
    boxShadow: '1px 1px 1px 1px rgba(0, 0, 0, 0.2)',
    border: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      '@media (hover: none)': {
        backgroundColor: theme.palette.primary.main
      }
    },
    '@media print': {
      backgroundColor: theme.palette.primary.main + ' !important',

      WebkitPrintColorAdjust: 'exact'
    }
  },
  handle: {
    fontSize: 11,
    marginTop: 0,
    color: 'white',
    textAlign: 'center',
    flex: '1 1 auto',

    '@media print': {
      color: 'white !important',
      fontWeight: 'bold !important',
      WebkitPrintColorAdjust: 'exact'
    }
  }
});

function HandleComponent({
  domain: [min, max],
  handle: { id, value = min, percent },
  classes,
  getHandleProps,
  allowNotApplicable
}) {
  const v = value === -1 && allowNotApplicable ? '' : value;
  return (
    <div
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      className={classes.root}
      style={{ left: `${percent}%` }}
      {...getHandleProps(id)}
    >
      <div className={classes.handle}>{v}</div>
    </div>
  );
}

HandleComponent.propTypes = {
  domain: PropTypes.array.isRequired,
  handle: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  }).isRequired,
  classes: PropTypes.object.isRequired,
  getHandleProps: PropTypes.func.isRequired
};

export const Handle = withStyles(handleStyle)(HandleComponent);

const trackStyle = (theme) => ({
  root: {
    position: 'absolute',
    height: 14,
    zIndex: 1,
    borderRadius: 7,
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    '@media print': {
      backgroundColor: theme.palette.primary.main + '!important',
      WebkitPrintColorAdjust: 'exact'
    }
  }
});

function TrackComponent({ classes, source, target, getTrackProps }) {
  return (
    <div
      className={classes.root}
      style={{
        left: `${source.percent}%`,
        width: `${target.percent - source.percent}%`
      }}
      {...getTrackProps()}
    />
  );
}

TrackComponent.propTypes = {
  source: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  }).isRequired,
  target: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  }).isRequired,
  classes: PropTypes.object.isRequired,
  getTrackProps: PropTypes.func.isRequired
};

export const Track = withStyles(trackStyle)(TrackComponent);

const tickStyle = (theme) => ({
  tick: {
    position: 'absolute',
    marginTop: 14,
    width: 1,
    height: 5,
    backgroundColor: theme.palette.text.primary,
    '@media print': {
      backgroundColor: theme.palette.text.primary + ' !important',
      WebkitPrintColorAdjust: 'exact'
    }
  },

  label: {
    position: 'absolute',
    marginTop: 22,
    textAlign: 'center',
    fontSize: 14
  }
});

export function TickComponent({ classes, tick, count, format, allowNotApplicable }) {
  // this means that if there are 12 or more ticks then it will switch to only showing alternating labels
  // this will reduce the label clutter with larger numbers of ticks and labels.
  const show = count < 13 || (tick.percent / Math.ceil(100 / count)) % 2 === 0;

  const value = tick.value === -1 && allowNotApplicable ? 'N/A' : tick.value;
  return (
    <div>
      <div className={classes.tick} style={{ left: `${tick.percent}%` }} />
      {show && (
        <Typography
          className={classes.label}
          style={{
            marginLeft: `${-(200 / count) / 2}%`,
            width: `${200 / count}%`,
            left: `${tick.percent}%`
          }}
        >
          {format(value)}
        </Typography>
      )}
    </div>
  );
}

TickComponent.propTypes = {
  tick: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    percent: PropTypes.number.isRequired
  }).isRequired,
  classes: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  format: PropTypes.func.isRequired
};

TickComponent.defaultProps = {
  format: (d) => d
};

export const Tick = withStyles(tickStyle)(TickComponent);
